<script lang="ts" module>
  /**
   * Die Rollen einer Marke.
   *
   * Bewusst nach **Bedeutung** benannt und nicht nach Farbe: `gut` statt
   * `gruen`. Wer die Farbe in den Namen schreibt, kann sie später nicht mehr
   * ändern, ohne überall zu lügen.
   */
  export type MarkenTon =
    | 'neutral'
    | 'gut'
    | 'achtung'
    | 'schlecht'
    | 'abo'
    | 'schnell'
    | 'notfall'
    | 'hotel'
    | 'camping'
    | 'stellplatz'
    // Der Kontaktstand eines Ausweichquartiers. Welcher Ton wozu gehört und
    // warum, steht an einer Stelle: `lib/quartierstand.ts`.
    | 'angefragt'
    | 'zugesagt'
    | 'abgesagt';
</script>

<script lang="ts">
  /**
   * Der Zustandschip — überall dort, wo ein Zustand, eine Kategorie oder eine
   * Stufe benannt wird.
   *
   * Vorher war das an sechs Stellen dasselbe Markup mit jeweils eigenem CSS:
   * in der Wetterübersicht, an den Ladestopps, in der Packliste, bei den
   * Fahrverbotszonen. Sie sahen ähnlich aus, aber nicht gleich — verschiedene
   * Radien, verschiedene Größen, und in einem Fall eine Farbe, die es im
   * Farbsatz gar nicht gab.
   *
   * Für die drei Zustände einer Fahrverbotszone gibt es `Zustandsmarke`: Die
   * trägt zusätzlich ein Zeichen, weil dort die Farbe allein nicht genügt.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    ton?: MarkenTon;
    children: Snippet;
  }
  let { ton = 'neutral', children }: Props = $props();
</script>

<span class="marke {ton}">{@render children()}</span>

<style>
  .marke {
    display: inline-flex;
    align-items: center;
    gap: var(--raum-2);
    padding: var(--raum-1) var(--raum-4);
    border-radius: var(--radius-rund);
    font-size: var(--fs-mikro);
    font-weight: var(--gw-halb);
    line-height: var(--zh-eng);
    white-space: nowrap;
  }

  .neutral { color: var(--text-dim); background: var(--surface-2); }
  .gut { color: var(--zustand-frei); background: var(--zustand-frei-flaeche); }
  .achtung { color: var(--zustand-pruefen); background: var(--zustand-pruefen-flaeche); }
  .schlecht { color: var(--zustand-meiden); background: var(--zustand-meiden-flaeche); }

  /* Ladesäulenstufen — im Abo bezahlt, schnell genug, oder Notnagel. */
  .abo { color: var(--stufe-abo); background: var(--zustand-frei-flaeche); }
  .schnell { color: var(--stufe-schnell); background: var(--surface-2); }
  .notfall { color: var(--stufe-notfall); background: var(--zustand-pruefen-flaeche); }

  /* Schlafplatzarten — gleichrangig, deshalb ohne Rangfolge in der Farbe. */
  .hotel { color: var(--kat-hotel); background: var(--surface-2); }
  .camping { color: var(--kat-camping); background: var(--zustand-frei-flaeche); }
  .stellplatz { color: var(--kat-stellplatz); background: var(--zustand-pruefen-flaeche); }

  /* Kontaktstand am Ausweichquartier — die `--buchung-*`-Token, die mit der
     Achse „Unterkunft“ in den Farbsatz kamen und bis hierher niemand benutzt
     hat. Warum „abgesagt“ das Token mit dem Namen „offen“ trägt (kurz: es ist
     frei, weil unser Vokabular kein „offen“ kennt, und sein Aussehen — ohne
     Farbe, zurückgenommen — ist genau das Richtige für eine Sackgasse), steht
     ausführlich in `lib/quartierstand.ts`. Rot wäre hier falsch: Eine Absage
     ist keine Gefahr. */
  .angefragt { color: var(--buchung-angefragt); background: var(--buchung-angefragt-flaeche); }
  .zugesagt { color: var(--buchung-gebucht); background: var(--buchung-gebucht-flaeche); }
  .abgesagt { color: var(--buchung-offen); background: var(--buchung-offen-flaeche); }
</style>
